/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import { DeviceType, useDevice } from '@squiz/dxp-console-support';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DEFAULT,
  Button,
  ButtonProps,
  ICON_UPLOAD,
  TOAST_TYPE_DANGER,
  TOAST_TYPE_SUCCESS,
  Toast,
  ToastProps,
} from '@squiz/sds';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { CDP_STRINGS } from '../../../../../../Constants/Strings.constants';
import {
  SingleCustomerViewServiceCreateExportDto,
  getScvService,
} from '../../../../../../Services/single-customer-view';
import { CUSTOMERS_DATA_IDS } from '../../../../SegmentInsights.constants';
import { ExportButtonProps } from './ExportButton.props';

/**
 * Button to initiate a CSV export of a customer segment.
 */
export const ExportButton = (props: ExportButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const { segment } = props;

  // Single Customer View service
  const singleCustomerViewService = getScvService();

  const { updateActiveToast } = useToastStore();

  // Handle navigation
  const navigate = useNavigate();
  const handleNavigation = useCallback(
    (itemPath: string): void => {
      navigate(itemPath);
    },
    [navigate],
  );

  // Device detection
  const deviceType = useDevice();
  const isMobile = deviceType === DeviceType.mobile;

  // States
  const [isLoading, setLoading] = useState(false);

  // Refs
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Labels
  const labels = useMemo(() => {
    return {
      error: t(CDP_STRINGS.error),
      exportError1: t(CDP_STRINGS.exportError1),
      exportError2: t(CDP_STRINGS.exportError2),
      exportInProgress1: t(CDP_STRINGS.exportInProgress1),
      exportInProgress2: t(CDP_STRINGS.exportInProgress2),
      generateExportAsCsv: t(CDP_STRINGS.generateExportAsCsv),
      generatingExport: t(CDP_STRINGS.generatingExport),
      viewExports: t(CDP_STRINGS.viewExports),
    };
  }, [t]);

  // Export customers as CSV
  const initiateExport = useCallback((): void => {
    setLoading(true);

    // Reset toast state
    updateActiveToast(null);

    const dto: SingleCustomerViewServiceCreateExportDto = { segment };
    const toastClass = 'customers__export-toast';

    lastValueFrom(singleCustomerViewService.createExport(dto))
      .then(() => {
        setLoading(false);

        const href = getCapabilityPath(`/exports`);
        const goToExports = (event: React.MouseEvent): void => {
          event.preventDefault();
          handleNavigation(href);
        };

        const successToastProps: ToastProps = {
          classes: toastClass,
          showToast: true,
          stateHandler: useToastStore,
          title: labels.generatingExport,
          toastContent: (
            <span className="customers__export-toast">
              {labels.exportInProgress1} <span className="customers__export-toast--segment">{segment}</span>{' '}
              {labels.exportInProgress2}{' '}
              <a href={href} onClick={goToExports}>
                {labels.viewExports}
              </a>
            </span>
          ),
          toastType: TOAST_TYPE_SUCCESS,
        };

        updateActiveToast(<Toast {...successToastProps} data-id={CUSTOMERS_DATA_IDS.exportToastSuccess} />);
        return;
      })
      .catch(() => {
        setLoading(false);

        const failToastProps: ToastProps = {
          classes: toastClass,
          showToast: true,
          stateHandler: useToastStore,
          title: labels.error,
          toastContent: (
            <span className="customers__export-toast">
              {labels.exportError1} <span className="customers__export-toast--segment">{segment}</span>{' '}
              {labels.exportError2}
            </span>
          ),
          toastType: TOAST_TYPE_DANGER,
        };

        updateActiveToast(<Toast {...failToastProps} data-id={CUSTOMERS_DATA_IDS.exportToastFailure} />);
      });
  }, [handleNavigation, labels, segment, singleCustomerViewService, updateActiveToast]);

  const canExport = hasPermission(PERMISSIONS.dxpSCV.CUSTOMERS_EXPORT);
  if (!canExport) {
    return <></>;
  }

  const buttonProps: ButtonProps = {
    classes: `customers__export-button`,
    dataId: CUSTOMERS_DATA_IDS.exportButton,
    format: BUTTON_FORMAT_SOFT,
    icon: ICON_UPLOAD,
    isLoading,
    isWide: isMobile,
    onClick: initiateExport,
    text: labels.generateExportAsCsv,
    theme: BUTTON_THEME_DEFAULT,
  };

  return (
    <div className="customers__export-container sds-tabs-outer-button">
      <Button {...buttonProps} ref={buttonRef} />
    </div>
  );
};
